
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const HormigonPulido = () => {
  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
  const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    const loadImages = async () => {
      try {
        const { data, error } = await supabase.storage
          .from('HormiPremium')
          .list('Pulido', {
            limit: 6,
            sortBy: { column: 'name', order: 'asc' },
          });

        if (error) throw error;

        if (data) {
          const imageFiles = data
            .filter((file) => {
              const ext = file.name.toLowerCase();
              return ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.png');
            })
            .slice(0, 6)
            .map((file) => {
              const fileName = encodeURIComponent(file.name);
              return `${SUPABASE_URL}/storage/v1/object/public/HormiPremium/Pulido/${fileName}`;
            });

          setImages(imageFiles);
        }
      } catch (error) {
        console.error('Error loading images:', error);
      }
    };

    loadImages();
  }, []);

  const features = [
    {
      icon: 'ri-contrast-drop-line',
      title: 'Acabado Brillante',
      description: 'Superficie lisa y pulida con acabado espejo de alta calidad',
    },
    {
      icon: 'ri-layout-grid-line',
      title: 'Sin Juntas',
      description: 'Pavimento continuo sin juntas de dilatación visibles',
    },
    {
      icon: 'ri-building-line',
      title: 'Ideal para Interiores',
      description: 'Perfecto para naves industriales, oficinas y espacios comerciales',
    },
    {
      icon: 'ri-brush-line',
      title: 'Fácil Limpieza',
      description: 'Superficie higiénica y de mantenimiento mínimo',
    },
  ];

  const applications = [
    'Naves industriales',
    'Oficinas y espacios comerciales',
    'Showrooms y concesionarios',
    'Centros comerciales',
    'Almacenes y logística',
    'Espacios públicos interiores',
  ];

  return (
    <section id="hormigon-pulido" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-contrast-drop-line"></i>
              <span>Acabado Profesional</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6 leading-tight">
              Hormigón Pulido
            </h2>
            <p className="text-secondary text-lg leading-relaxed mb-8">
              El <strong>hormigón pulido</strong> es la solución ideal para espacios que requieren un <strong>acabado continuo, liso y brillante</strong>. Perfecto para naves industriales, oficinas y espacios comerciales que buscan un aspecto moderno y profesional.
            </p>
            <p className="text-secondary text-lg leading-relaxed mb-8">
              Este pavimento ofrece una <strong>superficie sin juntas</strong>, fácil de limpiar y mantener, con una durabilidad excepcional que resiste el tráfico intenso y el uso industrial.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${feature.icon} text-slate-600 text-xl`}></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-dark mb-1">{feature.title}</h4>
                    <p className="text-secondary text-sm">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/servicios/hormigon-pulido"
              className="inline-flex items-center gap-2 px-8 py-4 bg-slate-600 text-white rounded-full font-semibold hover:bg-slate-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Ver detalles completos
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {images.length > 0 ? (
              images.slice(0, 4).map((image, index) => (
                <div
                  key={index}
                  className={`rounded-xl overflow-hidden shadow-lg ${
                    index === 0 ? 'col-span-2 h-80' : 'h-64'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Hormigón Pulido ${index + 1}`}
                    className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                  />
                </div>
              ))
            ) : (
              <div className="col-span-2 h-80 bg-gray-200 rounded-xl flex items-center justify-center">
                <p className="text-gray-500">Cargando imágenes...</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-8 md:p-12 shadow-lg">
          <h3 className="text-2xl font-bold text-dark mb-6">
            Aplicaciones del Hormigón Pulido
          </h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {applications.map((app, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 bg-white rounded-lg"
              >
                <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-slate-600 font-bold"></i>
                </div>
                <span className="text-dark font-medium">{app}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HormigonPulido;
